import { useMemo, useState } from "react";
import AMCReportTable from "./AMCReportTable";
import { Col, Row } from "react-bootstrap";
import { getFormatter } from "views/merchandise/metrics";
import { useAppSelector } from "redux/hooks";
import { formatAdType } from "./utils";
import { useAcosOrRoas } from "utils/acosOrRoas";
import { Button, ButtonGroup } from "@material-ui/core";
import PieChart from "components/core/blocks/PieChart";
import { metricColors } from "components/custom/analytics/palettes";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { useAMCReportData } from "./hooks/useAMCReportData";

const TOTAL_METRICS = [
  "ad_sales_clicks",
  "ad_sales_views",
  "ad_spend",
  "impressions",
  "clicks",
  "user_purchased_clicks",
  "conversions_clicks",
  "conversions_views",
];

const CHART_METRIC_OPTIONS = [
  {
    value: "ad_sales",
    label: "Ad Sales",
    format: "currency",
  },
  {
    value: "ad_spend",
    label: "Ad Spend",
    format: "currency",
  },
  {
    value: "clicks",
    label: "Clicks",
    format: "number",
  },
  {
    value: "impressions",
    label: "Impressions",
    format: "number",
  },
  {
    value: "conversions",
    label: "Conversions",
    format: "number",
  },
];

const Metric = ({ title, value, format }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const formatter = getFormatter(marketPlace, format);
  return (
    <Col key={title} className="fs-5 text-center align-items-flex-start">
      <span style={{ whiteSpace: "nowrap" }}>{title}</span>
      <span className="fs-3 fw-bold d-block">{formatter(value)}</span>
    </Col>
  );
};

const groupByKey = (select, data) => {
  if (!data) {
    return {};
  }
  const totalsByAdType = data.reduce((acc, row) => {
    const adType = select(row);
    if (!adType) {
      return acc;
    }

    if (!acc[adType]) {
      acc[adType] = {
        str_cnt: 0,
        ...TOTAL_METRICS.reduce((acc, metric) => {
          acc[metric] = 0;
          return acc;
        }, {}),
      };
    }
    acc[adType]["str_cnt"] += 1;
    TOTAL_METRICS.forEach((metric) => {
      acc[adType][metric] += row[metric] || 0;
    });
    return acc;
  }, {});
  Object.keys(totalsByAdType).forEach((adType) => {
    const total = totalsByAdType[adType];
    total["ad_sales"] = (total["ad_sales_clicks"] || 0) + (total["ad_sales_views"] || 0);
    total["roas"] = total["ad_sales"] / total["ad_spend"];
    total["acos"] = total["ad_spend"] / total["ad_sales"];
    total["ctr"] = total["clicks"] / total["impressions"];
    total["conversions"] = (total["conversions_clicks"] || 0) + (total["conversions_views"] || 0);
    total["conv_rate"] = total["conversions"] / total["clicks"];
    total["cost_per_click"] = total["ad_spend"] / total["clicks"];
  });
  return totalsByAdType;
};

const StackedBarChart = ({ metric, formatter, series, categories, height }) => {
  const options = {
    title: { text: "" },
    chart: {
      height,
      type: "bar",
      style: {
        fontFamily: "Poppins",
        fontWeight: 300,
        fontSize: "15px",
      },
    },
    colors: [metricColors.metric1[1], metricColors.metric2[1], metricColors.metric3[1], metricColors.metric4[1]],
    xAxis: {
      labels: {
        useHTML: true,
        allowOverlap: true,
        style: {
          wordBreak: "normal",
          textOverflow: "allow",
          width: "150px",
          minWidth: "150px",
          textAlign: "right",
        },
      },
      categories,
    },
    legend: { enabled: true },
    yAxis: {
      title: {
        text: metric,
      },
    },
    tooltip: {
      formatter: function () {
        if (formatter) {
          return `${this.x}: ${formatter(this.y)}`;
        }
        return `${this.x}: ${this.y}`;
      },
    },
    plotOptions: {
      series: {
        stacking: "normal",
        dataLabels: {
          enabled: false,
          formatter: function () {
            return formatter(this.y);
          },
        },
      },
    },
    series,
  };
  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const AMCSearchTermPlacementReport = ({ reportData, reportDetails, queryDetails }) => {
  const [toggle, setToggle] = useState("keywords", "totals");
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();
  const [pieChartMetric, setPieChartMetric] = useState(CHART_METRIC_OPTIONS[0].value);
  const selectedMetric = useMemo(
    () => CHART_METRIC_OPTIONS.find((option) => option.value === pieChartMetric),
    [pieChartMetric],
  );
  const { data: totalReportData } = useAMCReportData(
    null,
    "PLACEMENT_PERFORMANCE_INTERNAL",
    true,
    reportDetails?.id,
    !!reportDetails?.id,
  );
  const formatter = getFormatter(marketPlace, selectedMetric.format);

  const [byAdType, byKw] = useMemo(() => {
    const byAdType = groupByKey((row) => row.placement_type, totalReportData);
    const byKw = groupByKey((row) => JSON.stringify([row["customer_search_term"], row["placement_type"]]), reportData);
    return [byAdType, byKw];
  }, [reportData, totalReportData]);

  const calculatedReportData = useMemo(() => {
    return reportData.map((row) => {
      row["ad_sales"] = (row["ad_sales_clicks"] || 0) + (row["ad_sales_views"] || 0);
      row["roas"] = row["ad_sales"] / row["ad_spend"];
      row["acos"] = row["ad_spend"] / row["ad_sales"];
      row["click_thru_rate"] = row["clicks"] / row["impressions"];
      row["conversions"] = (row["conversions_clicks"] || 0) + (row["conversions_views"] || 0);
      row["conv_rate"] = row["conversions"] / row["clicks"];
      row["users_that_purchased"] = row["user_purchased_clicks"] || 0;
      row["cost"] = row["ad_spend"] || 0;
      row["cost_per_click"] = row["ad_spend"] / row["clicks"];
      return row;
    });
  }, [reportData]);

  const pieChartData = useMemo(() => {
    return (
      Object.keys(byAdType)
        .map((adType) => {
          const total = byAdType[adType];
          return {
            name: formatAdType(adType),
            y: total[pieChartMetric],
          };
        })
        ?.filter((type) => type.y) ?? []
    );
  }, [byAdType, pieChartMetric]);

  const rowChartData = useMemo(() => {
    if (!byKw) {
      return {
        categories: [],
        series: [],
      };
    }
    const mapping = Object.keys(byKw).reduce((acc, key) => {
      const total = byKw[key];

      const [kw, adType] = JSON.parse(key);
      if (!kw) {
        return acc;
      }
      if (!acc[kw]) {
        acc[kw] = {
          [adType]: 0,
        };
      }
      acc[kw][adType] = total[pieChartMetric];
      return acc;
    }, {});
    // total up the values

    const series = Object.keys(mapping).map((kw) => {
      const kwMap = mapping[kw];
      const total = Object.keys(kwMap).reduce((acc, adType) => {
        return acc + (kwMap[adType] || 0);
      }, 0);
      return { ...mapping[kw], total, kw };
    });
    // sort by total desc
    const sortedSeries = series.sort((a, b) => b.total - a.total).slice(0, 15);
    // create the series
    const categories = sortedSeries.map((item) => item.kw);
    return {
      categories,
      series: [
        { name: "Top Of Search on-Amazon", data: sortedSeries.map((item) => item["Top of Search on-Amazon"] || 0) },
        { name: "Other on-Amazon", data: sortedSeries.map((item) => item["Other on-Amazon"] || 0) },
        { name: "Detail Page on-Amazon", data: sortedSeries.map((item) => item["Detail Page on-Amazon"] || 0) },
        { name: "Off Amazon", data: sortedSeries.map((item) => item["Off Amazon"] || 0) },
      ],
    };
  }, [byKw, pieChartMetric]);

  return (
    <Row>
      <Col xs={12} xl={6}>
        <div className="d-flex justify-content-center align-items-center mt-2">
          <ButtonGroup>
            {[
              { value: "keywords", label: "Top Keywords" },
              { value: "totals", label: "Total" },
            ]?.map((group) => {
              const { value, label } = group;
              return (
                <Button
                  key={value}
                  onClick={() => setToggle(value)}
                  className="fs-6"
                  variant={toggle === value ? "contained" : null}
                  color={toggle === value ? "secondary" : null}
                >
                  {label}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
        <div className="py-5">
          {toggle === "totals" && (
            <PieChart
              data={pieChartData}
              nameAccessor={(d) => d.name}
              accessor={(d) => d.y}
              formatter={formatter}
              height={460}
            />
          )}
          {toggle === "keywords" && (
            <StackedBarChart
              height={500}
              metric={selectedMetric.label}
              formatter={formatter}
              series={rowChartData.series}
              categories={rowChartData.categories}
            />
          )}
        </div>
        <div className="d-flex justify-content-center align-items-center">
          <ButtonGroup>
            {CHART_METRIC_OPTIONS?.map((group) => {
              const { value, label } = group;
              return (
                <Button
                  key={value}
                  onClick={() => setPieChartMetric(value)}
                  className="fs-6"
                  variant={selectedMetric.value === value ? "contained" : null}
                  color={selectedMetric.value === value ? "secondary" : null}
                >
                  {label}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
      </Col>
      <Col xs={12} xl={6}>
        {Object.keys(byAdType)
          .sort((a, b) => {
            return byAdType[b]["impressions"] - byAdType[a]["impressions"];
          })
          .map((adType) => {
            const total = byAdType[adType];
            return (
              <div
                key={adType}
                className="me-4 p-4 mb-3"
                style={{
                  height: "150px",
                  boxShadow: "none",
                  border: "2px solid rgb(238, 238, 238)",
                  borderRadius: "25px",
                }}
              >
                <Row className="ps-4">
                  <Col xs={12} className="pb-1">
                    <span className="fs-4 fw-bold">{formatAdType(adType)}</span>
                  </Col>
                </Row>
                <Row className="d-flex h-100 align-items-center">
                  <Metric title="Ad Sales" value={total["ad_sales"]} format="currency" />
                  <Metric title="Ad Spend" value={total["ad_spend"]} format="currency" />
                  <Metric title="Impressions" value={total["impressions"]} format="number" />
                  <Metric title="Clicks" value={total["clicks"]} format="number" />
                  {acosOrRoas(
                    <Metric title="ACOS" value={total["acos"]} format="percent" />,
                    <Metric title="ROAS" value={total["roas"]} format="number" />,
                  )}
                  <Metric title="CTR" value={total["ctr"]} format="percent" />
                  <Metric title="Conversion Rate" value={total["conv_rate"]} format="percent" />
                  <Metric title="CPC" value={total["cost_per_click"]} format="currency" />
                </Row>
              </div>
            );
          })}
      </Col>
      <Col xs={12}>
        <AMCReportTable reportData={calculatedReportData} reportDetails={reportDetails} queryDetails={queryDetails} />
      </Col>
    </Row>
  );
};

export default AMCSearchTermPlacementReport;
